import { AbstractControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@neogov/pattern-library/core';

export type ShowErrorsWhen = 'touched' | 'dirty' | 'always';

/**
 * Determines whether a field's validation errors should currently be surfaced.
 *
 * Mirrors {@link FieldErrorsComponent.shouldShowErrors} so the visible
 * `<neo-error>` message and the programmatic error state (aria-invalid,
 * aria-describedby, invalid styling) stay in sync.
 */
export function shouldSurfaceFieldError(
  control: AbstractControl | null | undefined,
  showErrorsWhen: ShowErrorsWhen,
  hideErrors: boolean
): boolean {
  if (hideErrors || !control || !control.invalid) {
    return false;
  }

  switch (showErrorsWhen) {
    case 'always':
      return true;
    case 'dirty':
      return control.dirty;
    case 'touched':
    default:
      return control.touched;
  }
}

/**
 * ErrorStateMatcher that drives a `neoInput`/`neo-select` error state from an
 * external predicate instead of the inner control the pattern library sees.
 *
 * The `cm-*-field` wrappers attach validators to the OUTER `FormControl`, while
 * the native control is bound one-way via `[ngModel]`, so its own `NgModel`
 * carries no validators. Without this matcher the pattern library never enters
 * its error state, so `aria-invalid` stays `false` and the `<neo-error>` is
 * never linked to the control via `aria-describedby`. Feeding the outer
 * control's state through here restores correct assistive-technology behavior
 * (WCAG 3.3.1 Error Identification / 4.1.2 Name, Role, Value).
 */
export class OuterControlErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly _shouldShowError: () => boolean) {}

  public isErrorState(
    _control: AbstractControl | null,
    _form: FormGroupDirective | NgForm | null
  ): boolean {
    return this._shouldShowError();
  }
}
